import React from 'react';
import './DataCardList.css';
import { Header } from '@/Shared/Types/datatable.types';
import DataRowCard from '../DataRowCard/DataRowCard';
import DataListFilter from '../DataListFilter/DataListFilter';
import { useDataTableContext } from '@/Shared/Logic/Contexts/DataTableContext';
import Modal from '../../Modal/Modal';
import CreateButton from '../../Buttons/CreateButton/CreateButton';
import ExportButton from '../../Buttons/ExportButton/ExportButton';
import FilterToggleButton from '../../Buttons/FilterToggleButton/FilterToggleButton';
import LoadMoreButton from '../../Buttons/LoadMoreButton/LoadMoreButton';
import NoResultCard from '../../NoResultCard/NoResultCard';
import { useModal } from '@/Shared/Logic';
import { TrashButton } from '../../Buttons';
import { SearchOrder } from '@/Shared/Types';

export type SortedColumn<VM extends Record<string, any>> = {
	header: Header<VM>,
	order: SearchOrder
}

/**
 * Generic DataTable component with sorting, filtering, pagination, and customizable headers.
 * Uses a custom hook (useDataTable) to manage internal logic and state.
 *
 * Author: Samano CASTRE
 * Date: 29 June 2025
 */
function DataCardList<VM extends Record<string, any>>() {

	const {

		Icon,
		headers,
		slicedData,
		isFilterable,
		processedDataCount,
		activeFiltersCount,
		hasMoreData,
		batchCapacity,
		loadMoreData,
		actionHandlers,
		domainMode,
		newItems,
		title,
		headerAction,
		isRestorable
	} = useDataTableContext<VM>();

	const modalHook = useModal();

	const handleOpenFilter = () => {
		modalHook.openModal({
			title: "Filtres, Tri & Tranche",
			content: <DataListFilter />
		});
	}

	const isActive = domainMode?.current === 'active';

	return (
		<div className="DataCardList" data-testid="DataCardList">

			{/* Generic modal container (forms, details, etc.) */}
			<Modal
				onClose={modalHook.closeModal}
				data={modalHook.modalData}
			/>

			<div className='DataCardList__header flex-row'>

				<div className="sub-actions flex-row">
					{
						actionHandlers.handleCreate && (
							<CreateButton
								onClick={actionHandlers.handleCreate}
							/>
						)
					}
					{
						processedDataCount > 0
						&& actionHandlers.handleExcelExport
						&& (
							<ExportButton
								onClick={actionHandlers.handleExcelExport}
								count={processedDataCount}
							/>
						)
					}
					{domainMode && isRestorable && (
						<TrashButton
							isActive={isActive}
							onClick={domainMode.toggle}
						/>
					)}
				</div>

				{
					isFilterable && (
						<FilterToggleButton
							activeFilterCount={activeFiltersCount}
							onClick={handleOpenFilter}
						/>
					)
				}

			</div>
			<div className={`DataCardList__body ${domainMode?.current === 'active' ? 'active-mode' : 'trash-mode'}`}>
				{(title || headerAction) && <div className='DataCardList__header-wrapper'>
						{title && <h3 className='DataTable__title'>{title}</h3>}
						{headerAction}
					</div>
				}
				<div className="DataCardList__wrapper">
					{
						slicedData.map((row, index) =>
							<DataRowCard
							     newItems={newItems}
								row={row}
								key={index}
								headers={headers}
								Icon={Icon}
								onView={actionHandlers.handleRead}
								onEdit={actionHandlers.handleEdit}
								/**
								 * =====================================================
								 * Conditional Action Handlers
								 * =====================================================
								 *
								 * Description:
								 * The child component relies on the presence of an action handler
								 * to determine whether a corresponding button should be rendered.
								 * 
								 * Responsibilities:
								 * - If `handleDelete` or `handleRestore` exists, wrap it in an async function.
								 * - If the handler is missing, explicitly pass `undefined`.
								 *   This signals the child component NOT to render the button.
								 *
								 * Notes:
								 * - Passing `undefined` is intentional and meaningful for UI rendering logic.
								 * - Do NOT replace with a noop function; the child interprets presence vs absence.
								 */
								onDelete={
									actionHandlers.handleDelete
										? async () => actionHandlers.handleDelete?.(row.id)
										: undefined
								}
								onRestore={
									actionHandlers.handleRestore
										? async () => actionHandlers.handleRestore?.(row.id)
										: undefined
								}
							/>)
					}
				</div>

				{
					hasMoreData() &&
					<LoadMoreButton
						onClick={loadMoreData}
						batchCapacity={batchCapacity} />
				}

				{slicedData.length === 0 && <NoResultCard />}
			</div>
		</div>
	);
}

export default DataCardList;